import React from 'react';
import './Box.scss';
import '../grid/Grid.scss';

/**
 * Spacing scale values supported by Box component
 * Based on 0.25rem (4px) increments for medium devices (768px-1023px)
 *
 * Supported values:
 * 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 16, 20, 24, 28, 32, 36, 40
 *
 * Responsive behavior:
 * - xs (max 479px): 25% of medium value
 * - sm (480px-767px): 50% of medium value
 * - md (768px-1023px): 100% (base scale)
 * - lg (1024px-1279px): 150% of medium value
 * - xl (1280px-1535px): 200% of medium value
 * - xxl (1536px+): 300% of medium value
 *
 * Examples:
 * - m={1} = 0.25rem (4px) on md, 1px on xs, 2px on sm, 6px on lg, 8px on xl, 12px on xxl
 * - m={4} = 1rem (16px) on md, 4px on xs, 8px on sm, 24px on lg, 32px on xl, 48px on xxl
 * - m={8} = 2rem (32px) on md, 8px on xs, 16px on sm, 48px on lg, 64px on xl, 96px on xxl
 */
export type SpacingValue =
  | 0.5
  | 1
  | 1.5
  | 2
  | 2.5
  | 3
  | 3.5
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 14
  | 16
  | 20
  | 24
  | 28
  | 32
  | 36
  | 40;

export type BoxProps = Omit<
  React.HTMLAttributes<HTMLDivElement> & {
    /** Margin on all sides */
    m?: SpacingValue;
    /** Padding on all sides */
    p?: SpacingValue;
    /** Margin top */
    mt?: SpacingValue;
    /** Margin bottom */
    mb?: SpacingValue;
    /** Margin left */
    ml?: SpacingValue;
    /** Margin right */
    mr?: SpacingValue;
    /** Padding top */
    pt?: SpacingValue;
    /** Padding bottom */
    pb?: SpacingValue;
    /** Padding left */
    pl?: SpacingValue;
    /** Padding right */
    pr?: SpacingValue;
    /** Padding horizontal (left and right) */
    px?: SpacingValue;
    /** Padding vertical (top and bottom) */
    py?: SpacingValue;
    /** Margin horizontal (left and right) */
    mx?: SpacingValue;
    /** Margin vertical (top and bottom) */
    my?: SpacingValue;
    /** CSS display value */
    display?: React.CSSProperties['display'];
    /** CSS flex-direction value */
    flexDirection?: React.CSSProperties['flexDirection'];
    /** CSS align-items value */
    alignItems?: React.CSSProperties['alignItems'];
    /** CSS align-self value */
    alignSelf?: React.CSSProperties['alignSelf'];
    /** CSS justify-content value */
    justifyContent?: React.CSSProperties['justifyContent'];
    /** Border radius CSS variable (e.g., '--modus-radius-variable') */
    borderRadius?: string;
    /** Border color CSS variable (e.g., '--modus-color-primary-pale') */
    borderColor?: string;
    /** Background color CSS variable (e.g., '--modus-color-primary-pale') */
    backgroundColor?: string;
    /** Child elements */
    children?: React.ReactNode;
    /** Element type */
    as?: React.ElementType;
    /** Additional CSS class names */
    customClass?: string;
  },
  'flexGrow'
>;

/**
 * Convert spacing value to CSS class name format
 * Replaces decimal point with dash (e.g., 0.5 -> 0-5, 1.5 -> 1-5)
 */
export function getSpacingClass(prefix: string, value?: SpacingValue): string {
  if (value == null) return '';

  // Convert decimal to dash notation for CSS class name
  // e.g., 0.5 becomes "0-5", 1.5 becomes "1-5"
  const className = String(value).replace('.', '-');
  return `${prefix}-${className}`;
}

/**
 * Box Component
 *
 * A flexible container component with responsive spacing utilities.
 * Supports margin and padding props with a comprehensive spacing scale.
 *
 * @example
 * ```tsx
 * // Basic usage with margin and padding
 * <Box m={4} p={2}>Content</Box>
 *
 * // Using decimal values
 * <Box m={0.5} p={1.5}>Content</Box>
 *
 * // Directional spacing
 * <Box mt={2} mb={4} px={6}>Content</Box>
 *
 * // Using CSS variables for border and background
 * <Box
 *   borderRadius="--modus-radius-medium"
 *   borderColor="--modus-color-primary"
 *   backgroundColor="--modus-color-primary-pale"
 * >
 *   Content
 * </Box>
 *
 * // Combining with other HTML attributes
 * <Box m={2} p={4} className="custom-class" onClick={handleClick}>
 *   Content
 * </Box>
 * ```
 */
export const Box: React.FC<BoxProps> = ({
  m,
  p,
  mt,
  mb,
  ml,
  mr,
  pt,
  pb,
  pl,
  pr,
  px,
  py,
  mx,
  my,
  display,
  flexDirection,
  alignItems,
  alignSelf,
  justifyContent,
  borderRadius,
  borderColor,
  backgroundColor,
  customClass,
  children,
  className = '',
  style,
  as = 'div',
  ...rest
}) => {
  const spacingClasses = [
    'box',
    getSpacingClass('m', m),
    getSpacingClass('p', p),
    getSpacingClass('mt', mt),
    getSpacingClass('mb', mb),
    getSpacingClass('ml', ml),
    getSpacingClass('mr', mr),
    getSpacingClass('pt', pt),
    getSpacingClass('pb', pb),
    getSpacingClass('pl', pl),
    getSpacingClass('pr', pr),
    getSpacingClass('px', px),
    getSpacingClass('py', py),
    getSpacingClass('mx', mx),
    getSpacingClass('my', my),
  ].filter(Boolean);

  const allClasses = [...spacingClasses, customClass, className].filter(Boolean).join(' ');

  const flexStyle: React.CSSProperties = {};
  if (display !== undefined) flexStyle.display = display;
  if (flexDirection !== undefined) flexStyle.flexDirection = flexDirection;
  if (alignItems !== undefined) flexStyle.alignItems = alignItems;
  if (alignSelf !== undefined) flexStyle.alignSelf = alignSelf;
  if (justifyContent !== undefined) flexStyle.justifyContent = justifyContent;

  // Apply CSS variables for border and background
  if (borderRadius !== undefined) {
    flexStyle.borderRadius = `var(${borderRadius})`;
  }
  if (borderColor !== undefined) {
    flexStyle.borderColor = `var(${borderColor})`;
    flexStyle.borderWidth = '1px';
    flexStyle.borderStyle = 'solid';
  }
  if (backgroundColor !== undefined) {
    flexStyle.backgroundColor = `var(${backgroundColor})`;
  }

  const mergedStyle = Object.keys(flexStyle).length > 0 ? { ...flexStyle, ...style } : style;

  const Component = as;
  return (
    <Component className={allClasses} style={mergedStyle} {...rest}>
      {children}
    </Component>
  );
};

export default Box;
